import React, { FC, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { useSnackbar } from "notistack";
import { FormBaseType, FormBasicsDto } from "@green0meter/api-client";
import { useTranslation } from "react-i18next";

import { useCreateSubmission, useGetAvailableForms } from "@api";
import { MultistepForm } from "@components/common";
import { CircularProgress } from "@components/base";
import { useUserStore } from "@store/user";

const CsobForm: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUserStore();
  const [formBasics, setFormBasics] = useState<FormBasicsDto>();

  const {
    createSubmissionEnabled,
    isPendingCreateSubmission,
    createSubmissionError,
  } = useCreateSubmission(user);
  const { availableForms, isFetchingAvailableForms } = useGetAvailableForms(user, [
    FormBaseType.CsobClient,
    FormBaseType.CsobCommercialRealityAsset,
    FormBaseType.CsobEnergyAsset,
  ]);

  const finalizeSubmit = (): void => {
    navigate("/csob");
    enqueueSnackbar(t("csob.hub.csobForm.finalizeSubmit"), {
      variant: "info",
    });
  };

  useEffect(() => {
    const errorData = (createSubmissionError as AxiosError)?.response?.data as {
      message: string;
    };

    if (errorData) {
      enqueueSnackbar(errorData.message, { variant: "error" });
    }
  }, [createSubmissionError]);

  useEffect(() => {
    setFormBasics(availableForms?.find((f) => f.id === formId));
  }, [availableForms, formId]);

  return formId && isFetchingAvailableForms ? (
    <CircularProgress />
  ) : formBasics ? (
    <>
      <MultistepForm
        formSettings={{
          formBasics,
          returnUrl: "/csob",
          isCsobForm: true,
          button: createSubmissionEnabled
            ? {
                label: t("multistepForm.csob.submit"),
                onClick: finalizeSubmit,
                disableSubmit: true,
              }
            : undefined,
        }}
        isSubmissionPending={isPendingCreateSubmission}
      />
    </>
  ) : (
    "Form not found"
  );
};

export default CsobForm;
